import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { ConfigService } from "./config.service";
import { Observable } from "rxjs";

@Injectable()
export class FormsApiService {

  private get formsApiUrl(): string {
    return this.cs.formsApiEndPoint;
  }

  constructor(
    private http: HttpClient,
    private cs: ConfigService
  ) { }

  private getCompanyName(): string {
    const url = window.location.href;
    const urlParts = url.split("/");
    const printMatch = url.match(/print/g);
    const cMatch = url.includes("/c/");
    const positionToExtract = (printMatch && printMatch.length > 1) || cMatch ? 4 : 3;
    if (urlParts.length > positionToExtract) {
      return urlParts[positionToExtract] || "";
    }
    return "";
  }

  private jsonHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      "company-name": this.getCompanyName(),
    });
  }

  private companyOnlyHeaders(): HttpHeaders {
    return new HttpHeaders({
      "company-name": this.getCompanyName(),
    });
  }

  // ============================================
  // GET helpers
  // ============================================

  private get(path: string, params?: HttpParams): Observable<any> {
    return this.http.get(this.formsApiUrl + path, {
      headers: this.jsonHeaders(),
      params: params,
    });
  }

  // ============================================
  // POST helpers
  // ============================================

  private post(path: string, body: any): Observable<any> {
    return this.http.post(
      this.formsApiUrl + path,
      JSON.stringify(body),
      { headers: this.jsonHeaders() }
    );
  }

  private postFormData(path: string, formData: FormData): Observable<any> {
    return this.http.post(
      this.formsApiUrl + path,
      formData,
      { headers: this.companyOnlyHeaders() }
    );
  }

  // ============================================
  // DELETE helper
  // ============================================

  private deleteRequest(path: string, params?: HttpParams): Observable<any> {
    return this.http.delete(this.formsApiUrl + path, {
      headers: this.jsonHeaders(),
      params: params,
    });
  }

  // ============================================
  // Blob helpers
  // ============================================

  private postBlob(path: string, body: any): Observable<any> {
    return this.http.post(
      this.formsApiUrl + path,
      JSON.stringify(body),
      {
        headers: this.jsonHeaders(),
        responseType: "arraybuffer" as any,
      }
    );
  }

  // ============================================
  // Form Definition endpoints
  // ============================================

  getFormList(): Observable<any> {
    return this.get("/api/FormsEngine/GetFormList");
  }

  getFormDefinition(id: any): Observable<any> {
    return this.get("/api/FormsEngine/GetFormDefinition", new HttpParams().set("id", id));
  }

  getFormDefinitionForSubmit(urlCode: string): Observable<any> {
    return this.get("/api/FormsEngine/GetFormDefinitionForSubmit", new HttpParams().set("urlCode", urlCode));
  }

  getFieldTypes(): Observable<any> {
    return this.get("/api/FormsEngine/GetFieldTypes");
  }

  getDataSourceDefinitions(): Observable<any> {
    return this.get("/api/FormsEngine/GetDataSourceDefinitions");
  }

  // Admin Forms API signature (no urlCode). Timesheet_Web targets the Admin API,
  // which authorises via the Bearer token rather than a public urlCode.
  getFieldOptions(fieldId: number, search?: string | null, page: number = 1,
                  pageSize: number = 25, parentValue?: string | null): Observable<any> {
    let params = new HttpParams()
      .set("fieldId", String(fieldId))
      .set("page", String(page))
      .set("pageSize", String(pageSize));
    if (search) {
      params = params.set("search", search);
    }
    if (parentValue != null && parentValue !== "") {
      params = params.set("parentValue", parentValue);
    }
    return this.get("/api/FormsEngine/GetFieldOptions", params);
  }

  getDataSourceOptionsByCode(code: string, search?: string | null, page: number = 1,
                             pageSize: number = 50, parentValue?: string | null): Observable<any> {
    let params = new HttpParams()
      .set("code", code)
      .set("page", String(page))
      .set("pageSize", String(pageSize));
    if (search) {
      params = params.set("search", search);
    }
    if (parentValue != null && parentValue !== "") {
      params = params.set("parentValue", String(parentValue));
    }
    return this.get("/api/FormsEngine/GetDataSourceOptionsByCode", params);
  }

  formDefinitionCreate(formDefinition: any): Observable<any> {
    return this.post("/api/FormsEngine/FormDefinitionCreate", formDefinition);
  }

  formDefinitionSave(formDefinition: any): Observable<any> {
    return this.post("/api/FormsEngine/FormDefinitionSave", formDefinition);
  }

  // ============================================
  // Form Submission endpoints
  // ============================================

  getFormSubmission(id: any): Observable<any> {
    return this.get("/api/FormsEngine/GetFormSubmission", new HttpParams().set("id", id));
  }

  getFormSubmissionsList(formId: number, startDate?: string, endDate?: string): Observable<any> {
    let params = new HttpParams().set("formId", formId.toString());
    if (startDate) {
      params = params.set("startDate", startDate);
    }
    if (endDate) {
      params = params.set("endDate", endDate);
    }
    return this.get("/api/FormsEngine/GetFormSubmissionsList", params);
  }

  formSubmit(formDefinition: any): Observable<any> {
    return this.post("/api/FormsEngine/SaveFormSubmission", formDefinition);
  }

  submitCustomForm(formData: FormData): Observable<any> {
    return this.postFormData("/api/FormsEngine/SubmitCustomForm", formData);
  }

  // ============================================
  // Template endpoints
  // ============================================

  getFormTemplateBlob(templateFileId: number): Observable<Blob> {
    const params = new HttpParams().set("templateFileId", String(templateFileId));
    return this.http.get(this.formsApiUrl + "/api/FormsEngine/DownloadFormTemplate", {
      params,
      responseType: "blob",
      headers: this.companyOnlyHeaders(),
    });
  }

  uploadFormFieldTemplate(formId: number, formSectionId: number, fieldId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append("formId", formId.toString());
    formData.append("formSectionId", formSectionId.toString());
    formData.append("fieldId", fieldId.toString());
    formData.append("file", file, file.name);
    return this.postFormData("/api/FormsEngine/UploadFormFieldTemplate", formData);
  }

  // ============================================
  // Duplicate / Delete
  // ============================================

  duplicateForm(payload: any): Observable<any> {
    return this.post("/api/FormsEngine/DuplicateForm", payload);
  }

  deleteSection(sectionId: number): Observable<any> {
    return this.deleteRequest("/api/FormsEngine/DeleteSection", new HttpParams().set("sectionId", sectionId.toString()));
  }

  softDeleteForm(formId: number): Observable<any> {
    return this.deleteRequest("/api/FormsEngine/SoftDeleteForm", new HttpParams().set("formId", formId.toString()));
  }

  // ============================================
  // Export
  // ============================================

  exportFormSubmissionsToExcel(body: any): Observable<any> {
    return this.postBlob("/api/FormsEngine/ExportFormSubmissionsToExcel", body);
  }

  // ============================================
  // Trigger
  // ============================================

  getTriggerTypes(): Observable<any> {
    return this.get("/api/FormsEngine/GetTriggerTypes");
  }

  invokeTrigger(trigger: any): Observable<any> {
    return this.post("/api/FormsEngine/InvokeTriggerFromTicket", trigger);
  }

  // ============================================
  // OTP (Public form endpoints)
  // ============================================

  sendPublicFormOtp(body: any): Observable<any> {
    return this.post("/api/FormsEnginePublic/SendPublicFormOtp", body);
  }

  verifyPublicFormOtp(body: any): Observable<any> {
    return this.post("/api/FormsEnginePublic/VerifyPublicFormOtp", body);
  }
}
